import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';

interface NotificationPreferences {
  pushEnabled: boolean;
  dailyPuzzleEnabled: boolean;
  streakProtectionEnabled: boolean;
  achievementAlertsEnabled: boolean;
  quietHoursStart: string;
  quietHoursEnd: string;
  timezone: string;
}

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Check if VAPID key is configured
const isVapidConfigured = () => {
  const key = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  return typeof key === 'string' && key.length > 0;
};

export function useNotifications() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [canRequestPermission, setCanRequestPermission] = useState(false);
  const [vapidConfigured, setVapidConfigured] = useState(false);

  // Check if push notifications are supported AND VAPID is configured
  useEffect(() => {
    const checkSupport = () => {
      const vapidOk = isVapidConfigured();
      setVapidConfigured(vapidOk);
      
      if (!vapidOk) {
        // VAPID not configured - feature not ready
        setCanRequestPermission(false);
        setLoading(false);
        return;
      }
      
      if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        setCanRequestPermission(false);
        setLoading(false);
        return;
      }
      setCanRequestPermission(true);
    };
    checkSupport();
  }, []);

  // Load preferences
  const loadPreferences = useCallback(async () => {
    if (!user) {
      setPreferences(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setPreferences({
          pushEnabled: data.push_enabled,
          dailyPuzzleEnabled: data.daily_puzzle_enabled,
          streakProtectionEnabled: data.streak_protection_enabled,
          achievementAlertsEnabled: data.achievement_alerts_enabled,
          quietHoursStart: data.quiet_hours_start,
          quietHoursEnd: data.quiet_hours_end,
          timezone: data.timezone,
        });
      } else {
        // No preferences yet, use defaults
        setPreferences({
          pushEnabled: false,
          dailyPuzzleEnabled: true,
          streakProtectionEnabled: true,
          achievementAlertsEnabled: true,
          quietHoursStart: '22:00:00',
          quietHoursEnd: '08:00:00',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        });
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  // Request push notification permission
  const requestPermission = useCallback(async () => {
    if (!canRequestPermission || !user) {
      toast({
        title: "Not Available",
        description: "Push notifications are not supported on this device/browser",
        variant: "destructive",
      });
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // Register service worker and get push subscription
        const registration = await navigator.serviceWorker.ready;
        
        // Subscribe to push notifications
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            import.meta.env.VITE_VAPID_PUBLIC_KEY || ''
          ),
        });

        // Save subscription to database
        const { error } = await supabase
          .from('notification_preferences')
          .upsert({
            user_id: user.id,
            push_enabled: true,
            push_subscription: subscription.toJSON() as any,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          }, {
            onConflict: 'user_id'
          });

        if (error) throw error;

        await loadPreferences();
        
        toast({
          title: "🔔 Notifications Enabled!",
          description: "You'll get reminders to protect your streak",
        });
        
        return true;
      } else {
        toast({
          title: "Permission Denied",
          description: "You can enable notifications later in settings",
        });
        return false;
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast({
        title: "Error",
        description: "Failed to enable notifications. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [canRequestPermission, user, toast, loadPreferences]);

  // Update notification preferences
  const updatePreferences = useCallback(async (updates: Partial<NotificationPreferences>) => {
    if (!user || !preferences) return;

    try {
      const { error } = await supabase
        .from('notification_preferences')
        .upsert({
          user_id: user.id,
          push_enabled: updates.pushEnabled ?? preferences.pushEnabled,
          daily_puzzle_enabled: updates.dailyPuzzleEnabled ?? preferences.dailyPuzzleEnabled,
          streak_protection_enabled: updates.streakProtectionEnabled ?? preferences.streakProtectionEnabled,
          achievement_alerts_enabled: updates.achievementAlertsEnabled ?? preferences.achievementAlertsEnabled,
          quiet_hours_start: updates.quietHoursStart ?? preferences.quietHoursStart,
          quiet_hours_end: updates.quietHoursEnd ?? preferences.quietHoursEnd,
          timezone: updates.timezone ?? preferences.timezone,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      await loadPreferences();
      
      toast({
        title: "Preferences Updated",
        description: "Your notification settings have been saved",
      });
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      toast({
        title: "Error",
        description: "Failed to update preferences. Please try again.",
        variant: "destructive",
      });
    }
  }, [user, preferences, toast, loadPreferences]);

  // Disable all notifications
  const disableNotifications = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('notification_preferences')
        .update({
          push_enabled: false,
          daily_puzzle_enabled: false,
          streak_protection_enabled: false,
          achievement_alerts_enabled: false,
        })
        .eq('user_id', user.id);

      if (error) throw error;

      // Unsubscribe from push
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }

      await loadPreferences();
      
      toast({
        title: "Notifications Disabled",
        description: "You won't receive any notifications",
      });
    } catch (error) {
      console.error('Error disabling notifications:', error);
      toast({
        title: "Error",
        description: "Failed to disable notifications. Please try again.",
        variant: "destructive",
      });
    }
  }, [user, toast, loadPreferences]);

  return {
    preferences,
    loading,
    canRequestPermission,
    vapidConfigured,
    requestPermission,
    updatePreferences,
    disableNotifications,
  };
}
